import React, { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Button, Field, Input, Modal } from "../components/ui";

const FORM_VAZIO = {
    nome: "",
    email: "",
    cpf: "",
    telefone: "",
    cidade: "",
    bairro: "",
    logradouro: "",
    uf: "",
    numero: "",
    cep: ""
};

const CamposCliente = ({ dados, onChange, readOnly = false }) => (
    <div className="grid-form">
        <Field label="Nome do cliente">
            <Input name="nome" value={dados.nome || ""} onChange={onChange} readOnly={readOnly} required />
        </Field>
        <Field label="E-mail">
            <Input type="email" name="email" value={dados.email || ""} onChange={onChange} readOnly={readOnly} />
        </Field>
        <Field label="CPF">
            <Input name="cpf" value={dados.cpf || ""} onChange={onChange} maxLength={14} readOnly={readOnly} required />
        </Field>
        <Field label="Telefone">
            <Input type="tel" name="telefone" value={dados.telefone || ""} onChange={onChange} maxLength={11} readOnly={readOnly} />
        </Field>
        <Field label="Cidade">
            <Input name="cidade" value={dados.cidade || ""} onChange={onChange} readOnly={readOnly} required />
        </Field>
        <Field label="Bairro">
            <Input name="bairro" value={dados.bairro || ""} onChange={onChange} readOnly={readOnly} required />
        </Field>
        <Field label="Rua">
            <Input name="logradouro" value={dados.logradouro || ""} onChange={onChange} readOnly={readOnly} required />
        </Field>
        <Field label="Número">
            <Input name="numero" value={dados.numero || ""} onChange={onChange} readOnly={readOnly} required />
        </Field>
        <Field label="UF">
            <Input name="uf" value={dados.uf || ""} onChange={onChange} maxLength={2} readOnly={readOnly} required />
        </Field>
        <Field label="CEP">
            <Input name="cep" value={dados.cep || ""} onChange={onChange} maxLength={9} readOnly={readOnly} required />
        </Field>
    </div>
);

const Clientes = () => {
    const { podeEditar } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [busca, setBusca] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
    const [mostrarInfo, setMostrarInfo] = useState(false);
    const [mostrarAdd, setMostrarAdd] = useState(false);
    const [mostrarEdit, setMostrarEdit] = useState(false);
    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [formData, setFormData] = useState(FORM_VAZIO);

    const carregarClientes = useCallback(async () => {
        try {
            const response = await api.get("/clientes");
            setClientes(response.data);
        } catch (error) {
            console.error("Erro ao buscar clientes:", error);
        }
    }, []);

    useEffect(() => {
        carregarClientes();
    }, [carregarClientes]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const abrirEditar = (cliente) => {
        setFormData({
            nome: cliente.nome,
            email: cliente.email || "",
            cpf: cliente.CPF,
            telefone: cliente.telefone || "",
            cidade: cliente.cidade,
            bairro: cliente.bairro,
            logradouro: cliente.logradouro,
            uf: cliente.UF,
            numero: cliente.numero,
            cep: cliente.CEP
        });
        setErro(null);
        setMostrarInfo(false);
        setMostrarEdit(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);

        try {
            const cpfFormatado = formData.cpf.replace(/\D/g, "");
            if (!cpfFormatado.match(/^[0-9]{11}$/)) {
                throw new Error("CPF deve conter 11 dígitos.");
            }

            if (mostrarEdit && clienteSelecionado) {
                await api.put(`/clientes/${clienteSelecionado.id_cliente}`, { ...formData, cpf: cpfFormatado });
                setMostrarEdit(false);
            } else {
                await api.post("/clientes", { ...formData, cpf: cpfFormatado });
                setMostrarAdd(false);
            }

            carregarClientes();
            setFormData(FORM_VAZIO);
        } catch (err) {
            setErro(err.response?.data?.error || err.message);
        } finally {
            setCarregando(false);
        }
    };

    const excluirCliente = async (cliente) => {
        if (!window.confirm("Excluir este cliente?")) return;
        try {
            await api.delete(`/clientes/${cliente.id_cliente}`);
            setClientes((prev) => prev.filter((c) => c.id_cliente !== cliente.id_cliente));
            setMostrarInfo(false);
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível excluir o cliente.");
        }
    };

    const clientesFiltrados = clientes.filter((c) =>
        c.nome.toLowerCase().includes(busca.toLowerCase()) ||
        c.CPF.toLowerCase().includes(busca.toLowerCase())
    );

    return (
        <div>
            <Header />

            <main className="page">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Clientes</h2>
                    <div className="toolbar-spacer">
                        <Input placeholder="Buscar por nome ou CPF" value={busca} onChange={(e) => setBusca(e.target.value)} />
                        {podeEditar && <Button onClick={() => { setFormData(FORM_VAZIO); setErro(null); setMostrarAdd(true); }}>Novo cliente</Button>}
                    </div>
                </div>

                <div className="grid-cards">
                    {clientesFiltrados.map((cliente) => (
                        <div
                            key={cliente.id_cliente}
                            className="card clicavel"
                            style={{ cursor: "pointer" }}
                            onClick={() => { setMostrarInfo(true); setClienteSelecionado(cliente); }}
                        >
                            <span className="card-kicker">CPF {cliente.CPF}</span>
                            <span className="card-title">{cliente.nome}</span>
                            <p className="card-body">
                                {cliente.cidade}/{cliente.UF}
                                {cliente.total_contratos > 0
                                    ? ` · ${cliente.total_contratos} contrato${cliente.total_contratos > 1 ? "s" : ""}`
                                    : " · sem contratos"}
                            </p>
                        </div>
                    ))}
                    {clientesFiltrados.length === 0 && (
                        <p className="text-muted">Nenhum cliente encontrado.</p>
                    )}
                </div>

                {mostrarAdd && (
                    <Modal titulo="Novo cliente" onFechar={() => setMostrarAdd(false)}>
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <CamposCliente dados={formData} onChange={handleChange} />
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => setMostrarAdd(false)}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}

                {mostrarInfo && clienteSelecionado && (
                    <Modal titulo={clienteSelecionado.nome} onFechar={() => setMostrarInfo(false)}>
                        <CamposCliente
                            readOnly
                            dados={{
                                nome: clienteSelecionado.nome,
                                email: clienteSelecionado.email,
                                cpf: clienteSelecionado.CPF,
                                telefone: clienteSelecionado.telefone,
                                cidade: clienteSelecionado.cidade,
                                bairro: clienteSelecionado.bairro,
                                logradouro: clienteSelecionado.logradouro,
                                uf: clienteSelecionado.UF,
                                numero: clienteSelecionado.numero,
                                cep: clienteSelecionado.CEP
                            }}
                        />
                        <div className="modal-acoes">
                            {podeEditar && <Button variante="danger" onClick={() => excluirCliente(clienteSelecionado)}>Excluir</Button>}
                            <Button variante="secondary" onClick={() => setMostrarInfo(false)}>Voltar</Button>
                            {podeEditar && <Button onClick={() => abrirEditar(clienteSelecionado)}>Editar</Button>}
                        </div>
                    </Modal>
                )}

                {mostrarEdit && clienteSelecionado && (
                    <Modal titulo="Editar cliente" onFechar={() => setMostrarEdit(false)}>
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <CamposCliente dados={formData} onChange={handleChange} />
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => { setMostrarEdit(false); setMostrarInfo(true); }}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}
            </main>
        </div>
    );
};

export default Clientes;
